import {
  Box,
  Card,
  CardContent,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Eye,
  MousePointerClick,
  ShoppingCart,
  Target,
  Info,
} from 'lucide-react';
import React from 'react';
import {
  DailySSMetricsV2,
  DirectCVMetrics,
} from '../../../services/shelf-service';

interface MetricsSummaryCardsProps {
  data: DailySSMetricsV2[];
  compareData: DailySSMetricsV2[];
  showComparison: boolean;
  shelfUnitPrice: number;
  otherUnitPrice: number;
  directCVData?: DirectCVMetrics[];
  directCVCompareData?: DirectCVMetrics[];
}

const MetricsSummaryCards: React.FC<MetricsSummaryCardsProps> = ({
  data,
  compareData,
  showComparison,
  shelfUnitPrice,
  otherUnitPrice,
  directCVData,
  directCVCompareData,
}) => {
  const calculateTotal = (
    metric: keyof DailySSMetricsV2,
    targetData: DailySSMetricsV2[],
  ): number => {
    return targetData.reduce((sum, day) => sum + (Number(day[metric]) || 0), 0);
  };

  const calculateRevenue = (targetData: DailySSMetricsV2[]): number => {
    return calculateTotal('shelf_cv_ss', targetData) * shelfUnitPrice;
  };

  const calculateTotalRevenue = (targetData: DailySSMetricsV2[]): number => {
    const shelfRevenue =
      calculateTotal('shelf_cv_ss', targetData) * shelfUnitPrice;
    const otherRevenue =
      (calculateTotal('cv_ss', targetData) -
        calculateTotal('shelf_cv_ss', targetData)) *
      otherUnitPrice;
    return shelfRevenue + otherRevenue;
  };

  const calculateRates = (targetData: DailySSMetricsV2[]) => {
    const totalSS = calculateTotal('ss', targetData);
    const shelfShown = calculateTotal('shown_ss', targetData);
    const shelfClick = calculateTotal('click_ss', targetData);
    const totalRevenue = calculateTotalRevenue(targetData);
    const shelfRevenue = calculateRevenue(targetData);

    return {
      shownRate: totalSS > 0 ? (shelfShown / totalSS) * 100 : 0,
      clickRate: shelfShown > 0 ? (shelfClick / shelfShown) * 100 : 0,
      revenueRate: totalRevenue > 0 ? (shelfRevenue / totalRevenue) * 100 : 0,
    };
  };

  const calculateDirectRevenue = (targetData: DirectCVMetrics[]): number => {
    return targetData.reduce(
      (sum, day) => sum + (Number(day.direct_cv_revenue) || 0),
      0,
    );
  };

  const calculateDirectCVCount = (targetData: DirectCVMetrics[]): number => {
    return targetData.reduce(
      (sum, day) => sum + (Number(day.direct_cv_count) || 0),
      0,
    );
  };

  const calculateDirectCVRate = (targetData: DirectCVMetrics[]): number => {
    const directRevenue = targetData.reduce(
      (sum, day) => sum + (Number(day.direct_cv_revenue) || 0),
      0,
    );
    const totalRevenue = targetData.reduce(
      (sum, day) => sum + (Number(day.total_cv_revenue) || 0),
      0,
    );
    return totalRevenue > 0 ? (directRevenue / totalRevenue) * 100 : 0;
  };

  const calculateComparisonRate = (
    current: number,
    previous: number,
  ): number => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const rates = calculateRates(data);

  // 比較期間を計算する関数
  const getComparisonPeriod = (): string => {
    if (!showComparison || compareData.length === 0) return '';

    const sortedCompareData = [...compareData].sort((a, b) =>
      a.ymd.localeCompare(b.ymd),
    );
    const startDate = sortedCompareData[0]?.ymd;
    const endDate = sortedCompareData[sortedCompareData.length - 1]?.ymd;

    if (!startDate || !endDate) return '';

    return `（対 ${startDate.replace(/-/g, '/')}~${endDate.replace(/-/g, '/')}）`;
  };

  const renderComparisonValue = (current: number, previous: number) => {
    if (!showComparison) return null;
    const rate = calculateComparisonRate(current, previous);
    const color = rate >= 0 ? '#4CAF50' : '#f44336';
    const comparisonPeriod = getComparisonPeriod();

    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color,
            display: 'block',
            fontWeight: 'bold',
          }}
        >
          {rate >= 0 ? '▲' : '▼'} {Math.abs(rate).toFixed(1)}%{' '}
          <Typography
            component="span"
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 'normal',
            }}
          >
            {comparisonPeriod}
          </Typography>
        </Typography>
      </Box>
    );
  };

  const metrics = [
    {
      title: '見た',
      icon: Eye,
      color: '#2196F3',
      value: calculateTotal('shown_ss', data),
      compareValue: showComparison
        ? calculateTotal('shown_ss', compareData)
        : null,
      label: 'シェルフIMPSS数',
      subLabel: `サイト全体SSの${rates.shownRate.toFixed(1)}%`,
      tooltip: 'ユーザーの画面にシェルフが表示されたセッションの数',
    },
    {
      title: '使った',
      icon: MousePointerClick,
      color: '#4CAF50',
      value: calculateTotal('click_ss', data),
      compareValue: showComparison
        ? calculateTotal('click_ss', compareData)
        : null,
      label: 'シェルフクリックSS数',
      subLabel: `クリック率: ${rates.clickRate.toFixed(1)}%`,
      tooltip: 'シェルフをクリックしたセッションの数',
      clickRateTooltip:
        'シェルフクリックSS数/シェルフIMPSS数。シェルフが表示された際のクリック率を表す',
    },
    {
      title: '買った',
      icon: ShoppingCart,
      color: '#FF9800',
      values: [
        {
          label: 'シェルフ経由CV SS数',
          value: calculateTotal('shelf_cv_ss', data),
          compareValue: showComparison
            ? calculateTotal('shelf_cv_ss', compareData)
            : null,
          tooltip:
            'シェルフをクリックした後に同じセッション内で購入に至ったセッション数',
        },
        {
          label: 'シェルフ経由売上高',
          value: calculateRevenue(data),
          compareValue: showComparison ? calculateRevenue(compareData) : null,
          isRevenue: true,
          subLabel: `サイト全体売上の${rates.revenueRate.toFixed(1)}%`,
          tooltip:
            'シェルフをクリックした後に同じセッション内で購入に至った売上金額',
        },
      ],
    },
    ...(directCVData && directCVData.length > 0
      ? [
          {
            title: '直接買った',
            icon: Target,
            color: '#9C27B0',
            values: [
              {
                label: '直接CV件数',
                value: calculateDirectCVCount(directCVData),
                compareValue:
                  showComparison && directCVCompareData
                    ? calculateDirectCVCount(directCVCompareData)
                    : null,
                tooltip:
                  'シェルフでクリックした商品をそのまま購入した件数（品番単位）。クリックから7日以内の購入のみを対象',
              },
              {
                label: '直接売上高',
                value: calculateDirectRevenue(directCVData),
                compareValue:
                  showComparison && directCVCompareData
                    ? calculateDirectRevenue(directCVCompareData)
                    : null,
                isRevenue: true,
                subLabel: `サイト全体売上の${calculateDirectCVRate(directCVData).toFixed(1)}%`,
                tooltip:
                  'シェルフでクリックした商品をそのまま購入した売上金額。クリックから7日以内の購入のみを対象',
              },
            ],
          },
        ]
      : []),
  ];

  const hasDirectCVData = directCVData && directCVData.length > 0;
  const gridColumns = hasDirectCVData ? 4 : 3;
  const isLastColumn = (title: string) =>
    hasDirectCVData ? title === '直接買った' : title === '買った';

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${gridColumns}, 1fr)`,
          },
          gap: 3,
          mb: 2,
        }}
      >
        {metrics.map((metric) => (
          <Box
            key={metric.title}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: '-1.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '10px solid transparent',
                borderBottom: '10px solid transparent',
                borderLeft: '10px solid rgba(0, 0, 0, 0.1)',
                display: {
                  xs: 'none',
                  sm: isLastColumn(metric.title) ? 'none' : 'block',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: `${metric.color}15`,
                color: metric.color,
              }}
            >
              <metric.icon size={28} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {metric.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: -0.5,
                }}
              >
                {metric.title === '見た' && '露出度'}
                {metric.title === '使った' && '魅力度'}
                {metric.title === '買った' && 'CV決定力'}
                {metric.title === '直接買った' && '直接効果'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${gridColumns}, 1fr)`,
          },
          gap: 3,
        }}
      >
        {metrics.map((metric) => (
          <Card
            key={metric.title}
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              },
              height: '100%',
              display: 'flex',
            }}
          >
            <CardContent
              sx={{
                p: 3,
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {'values' in metric ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        {metric.values[0].label}
                      </Typography>
                      <Tooltip
                        title={metric.values[0].tooltip}
                        arrow
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          sx={{
                            color: 'text.secondary',
                            padding: '2px',
                            '&:hover': {
                              color: 'primary.main',
                            },
                          }}
                        >
                          <Info size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {metric.values[0].value.toLocaleString()}
                    </Typography>
                    {renderComparisonValue(
                      metric.values[0].value,
                      metric.values[0].compareValue || 0,
                    )}
                  </Box>
                  <Box
                    sx={{
                      pt: 3,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: 'text.secondary' }}
                        >
                          {metric.values[1].label}
                        </Typography>
                        <Tooltip
                          title={metric.values[1].tooltip}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              padding: '2px',
                              '&:hover': {
                                color: 'primary.main',
                              },
                            }}
                          >
                            <Info size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        {metric.values[1].isRevenue
                          ? `¥${metric.values[1].value.toLocaleString()}`
                          : metric.values[1].value.toLocaleString()}
                      </Typography>
                      {renderComparisonValue(
                        metric.values[1].value,
                        metric.values[1].compareValue || 0,
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {metric.values[1].subLabel.split('の')[0]}の
                        <Typography
                          component="span"
                          sx={{
                            color: '#1a1a1a',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                          }}
                        >
                          {metric.values[1].subLabel.split('の')[1]}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                      {metric.label}
                    </Typography>
                    <Tooltip title={metric.tooltip} arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{
                          color: 'text.secondary',
                          padding: '2px',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        <Info size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {metric.value.toLocaleString()}
                  </Typography>
                  {renderComparisonValue(
                    metric.value,
                    metric.compareValue || 0,
                  )}
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {metric.subLabel.includes('クリック率') ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          justifyContent: 'center',
                        }}
                      >
                        <span>クリック率: </span>
                        <Typography
                          component="span"
                          sx={{
                            color: '#1a1a1a',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                          }}
                        >
                          {rates.clickRate.toFixed(1)}%
                        </Typography>
                        <Tooltip
                          title={metric.clickRateTooltip}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              padding: '1px',
                              minWidth: 'auto',
                              '&:hover': {
                                color: 'primary.main',
                              },
                            }}
                          >
                            <Info size={12} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <>
                        {`${metric.subLabel.split('の')[0]}の`}
                        <Typography
                          component="span"
                          sx={{
                            color: '#1a1a1a',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                          }}
                        >
                          {rates.shownRate.toFixed(1)}%
                        </Typography>
                      </>
                    )}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default MetricsSummaryCards;
